/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Link, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { i18n } from 'src/common/i18n/i18n';
import { CustomInfiniteScrollList, FiltersPanel } from 'src/common/components';
import { socialPlatformsUserFriendlyDisplayName } from 'src/common/constants/constants';
import { capitalizeFirstLetter } from 'src/common/utils/formatters';
import MatchWideCard from './components/MatchWideCard/MatchWideCard';
import MatchCard from './components/MatchCard/MatchCard';
import { Tooltip, SmartImage, COLORS } from '@cellebrite/saas-ui-common';
import { getPlatformImage } from '../../../../utils/utils';
import FilterInfo from '../../../../components/FilterInfo/FilterInfo';

interface MatchesSectionProps {
    findInput?: string;
    platform: string;
    accounts: any;
    filters: any;
    fallback?: boolean;
    onCheckedChange: any;
    filterByPlatform: any;
    setFilterByPlatform: any;
    setClickedAccount: any;
    searchInformation: any;
}
const PLATFORM = {
    PIPL: 'pipl',
    LEXISNEXIS: 'lexisnexis',
    ThatsThem: 'thatsthem',
    TWITTER: 'twitter',
    INSTAGRAM: 'instagram',
    PINTEREST: 'pinterest',
    FACEBOOK: 'facebook',
    LINKEDIN: 'linkedin',
    XING: 'xing',
};
const MatchesSection: React.FC<MatchesSectionProps> = ({
    findInput,
    platform,
    accounts,
    filters: advancedFilters,
    fallback,
    onCheckedChange,
    filterByPlatform,
    setFilterByPlatform,
    setClickedAccount,
    searchInformation,
}) => {
    const filtersRef = useRef(null);
    const [sectionAccounts, setSectionAccounts] = useState(accounts);
    const isWiderCard = [PLATFORM.PIPL, PLATFORM.LEXISNEXIS, PLATFORM.ThatsThem].includes(platform);
    const onChange = (checked, account) => {
        if (onCheckedChange) {
            onCheckedChange(platform, checked, account);
        }
    };

    const FILTERS = {
        LOCATION: 'location',
        COUNTRY: 'country',
        STATE: 'state',
        CITY: 'city',
        YEAR_OF_BIRTH: 'year of birth',
        GENDER: 'gender',
        WEBSITE: 'website',
        PUBLIC: 'public',
        WORKPLACE: 'workplace',
        EDUCATION: 'education',
    };
    const getFiltersByPlatform = () => {
        const filters = [];
        const advancedFilterKeys = advancedFilters?.map((advancedFilter) => advancedFilter.key.toLowerCase());
        function addFilter(filters: any[], type: string) {
            if (!advancedFilterKeys?.includes(type.toLowerCase())) {
                filters.push(type);
            }
        }

        switch (platform) {
            case PLATFORM.PIPL:
                addFilter(filters, FILTERS.COUNTRY);
                addFilter(filters, FILTERS.STATE);
                addFilter(filters, FILTERS.CITY);
                addFilter(filters, FILTERS.YEAR_OF_BIRTH);
                addFilter(filters, FILTERS.GENDER);
                break;
            case PLATFORM.LEXISNEXIS:
                break;
            case PLATFORM.TWITTER:
                addFilter(filters, FILTERS.LOCATION);
                addFilter(filters, FILTERS.WEBSITE);
                addFilter(filters, FILTERS.PUBLIC);
                break;
            case PLATFORM.INSTAGRAM:
                addFilter(filters, FILTERS.PUBLIC);
                break;
            case PLATFORM.PINTEREST:
                break;
            case PLATFORM.FACEBOOK:
                addFilter(filters, FILTERS.LOCATION);
                addFilter(filters, FILTERS.WORKPLACE);
                addFilter(filters, FILTERS.EDUCATION);
                break;
            case PLATFORM.LINKEDIN:
                addFilter(filters, FILTERS.LOCATION);
                break;
            case PLATFORM.XING:
                addFilter(filters, FILTERS.LOCATION);
                break;
        }
        return filters;
    };
    const getOptions = (accounts, filter) => {
        const result = [];
        const isLocations = filter === 'city' || filter === 'country' || filter === 'state';
        const isDob = filter === 'year of birth';
        const yearOfBirthKey = 'year';
        const getPiplDataPath = (account) =>
            isLocations ? account.overview.locations : isDob ? account.overview.dob : account.overview[filter];
        const groupByPath = isLocations
            ? `overview.locations.value.addressDetails.${filter}`
            : isDob
              ? `overview.dob.value.${yearOfBirthKey}`
              : `overview.${filter}.value`;
        const groupByAccounts = accounts.filter((account) =>
            platform === PLATFORM.PIPL ? getPiplDataPath(account) : account[filter],
        );

        function mapPiplAccounts(groupByAccounts: any) {
            const result = [];
            let uniqueValues = {};
            const groupKey = isDob ? 'dob' : 'locations';
            groupByAccounts.forEach((account) => {
                uniqueValues = {};
                account.overview[groupKey]?.forEach((groupName) => {
                    const path = isDob ? groupName.value.year : groupName.value.addressDetails[filter];
                    if (!uniqueValues[path] && path) {
                        uniqueValues[path] = true;
                        const resultAccount = _.cloneDeep(account);
                        resultAccount.overview[groupKey] = groupName;
                        result.push(resultAccount);
                    }
                });
            });
            return result;
        }

        _.forOwn(
            _.groupBy(
                platform === PLATFORM.PIPL && (isLocations || isDob)
                    ? mapPiplAccounts(groupByAccounts)
                    : groupByAccounts,
                platform === PLATFORM.PIPL ? groupByPath : filter,
            ),
            (group, name) => {
                result.push({
                    label: name,
                    count: group.length,
                    value: name,
                });
            },
        );
        return _.orderBy(result, ['count', 'label'], ['desc', 'asc']);
    };
    const getFilters = (accounts) => {
        const filters = getFiltersByPlatform();
        const result = [];
        filters.forEach((filter) => {
            result.push({
                name: capitalizeFirstLetter(filter),
                originalName: filter,
                options: getOptions(accounts, filter),
                selectionType: 'multi',
                searchableOptions: true,
            });
        });
        return result;
    };
    useEffect(() => {
        if (accounts) {
            if (filtersRef.current?.filtersState) {
                setSectionAccounts(accounts.filter((account) => _.find(sectionAccounts, { id: account.id })));
            } else {
                setSectionAccounts(accounts);
            }
        }
    }, [accounts]);

    function getFilterPiplBy(account, filter: any) {
        return account.overview[filter.key];
    }

    function getFilteredAccounts(accounts: any, filters: any[]) {
        return accounts.filter((account) => {
            const accountAppliedFilters = filters.filter((filter) => {
                let result = false;
                const isYearOfBirth = filter.key === 'year of birth';
                if (platform === PLATFORM.PIPL) {
                    if (filter.key === 'city' || filter.key === 'country' || filter.key === 'state' || isYearOfBirth) {
                        const groupKey = isYearOfBirth ? 'dob' : 'locations';
                        account.overview[groupKey].forEach((groupName) => {
                            if (
                                filter.value.includes(
                                    isYearOfBirth
                                        ? groupName.value['year']
                                        : groupName.value.addressDetails[filter.key],
                                )
                            ) {
                                result = true;
                            }
                        });
                    } else if (filter.value.includes(getFilterPiplBy(account, filter)?.value)) {
                        result = true;
                    }
                } else {
                    if (filter.value.includes(account[filter.key])) {
                        result = true;
                    }
                }
                return result;
            });
            return filters.length === accountAppliedFilters.length;
        });
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {!!sectionAccounts?.length && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'start',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                    }}
                >
                    <Tooltip
                        content={
                            <Typography className="color-mh">
                                {socialPlatformsUserFriendlyDisplayName.get(platform.toUpperCase()) ||
                                    capitalizeFirstLetter(platform.toUpperCase())}
                            </Typography>
                        }
                    >
                        <Box sx={{ mt: 1 }}>
                            <img src={getPlatformImage(platform)} width={24} height={24} />
                        </Box>
                    </Tooltip>
                    <Box sx={{ display: 'flex', columnGap: 1, ml: 3, height: 40 }}>
                        {advancedFilters?.map((advancedFilter) => (
                            <FilterInfo label={advancedFilter.key} value={advancedFilter.value} />
                        ))}
                        {accounts?.length > 1 && (
                            <FiltersPanel
                                hideChips={true}
                                ref={filtersRef}
                                filters={getFilters(sectionAccounts)}
                                onApplyFilters={(filtersToApply) => {
                                    const filters = [];
                                    if (filtersToApply) {
                                        _.forOwn(filtersToApply, (group: any, key) => {
                                            if (group?.length) {
                                                filters.push({ key: key, value: group.map((value) => value.value) });
                                            }
                                        });
                                    }
                                    if (filters.length) {
                                        setSectionAccounts(getFilteredAccounts(accounts, filters));
                                    } else {
                                        setSectionAccounts(accounts);
                                    }
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}
            {fallback && (
                <Typography
                    variant="s14w700"
                    sx={{
                        color: COLORS.MUTED_DARK,
                        backgroundColor: COLORS.MUTED_SOFT,
                        borderRadius: '4px',
                        px: 1,
                        width: 'fit-content',
                        mt: 1,
                    }}
                >
                    {i18n.translate('No results found using advanced search. ‘Name’ search results displayed.')}
                </Typography>
            )}
            <Box sx={{ mt: 2 }}>
                <CustomInfiniteScrollList
                    horizontal={filterByPlatform ? false : true}
                    disableWrap={filterByPlatform ? false : true}
                    styles={{ maxHeight: filterByPlatform ? 607 : null }}
                    gap={2}
                    list={sectionAccounts.map((account, index) =>
                        isWiderCard ? (
                            <MatchWideCard
                                key={index}
                                showConfidence={searchInformation.advancedSearchInputs?.length}
                                findInput={findInput}
                                account={account}
                                platform={platform}
                                onCheckedChange={onChange}
                                setClickedAccount={setClickedAccount}
                            />
                        ) : (
                            <MatchCard
                                key={index}
                                findInput={findInput}
                                account={account}
                                platform={platform}
                                onCheckedChange={onChange}
                                setClickedAccount={setClickedAccount}
                            />
                        ),
                    )}
                />
            </Box>
            <Box
                sx={{
                    textAlign: 'right',
                    visibility:
                        sectionAccounts?.length > (platform === 'pipl' ? 3 : 6) && !filterByPlatform
                            ? 'visible'
                            : 'hidden',
                }}
            >
                <Link onClick={() => setFilterByPlatform(platform)}>{`${i18n.translate(
                    'View all',
                )} (${sectionAccounts?.length})`}</Link>
            </Box>
        </Box>
    );
};

export default MatchesSection;
